import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

// Anillos concéntricos que respiran (solo transform/opacity) mientras el motor arma la rutina.
const ringPulse = keyframes`
  0% { transform: scale(0.92); opacity: 0.35; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.92); opacity: 0.35; }
`;

const phraseIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const PHRASE_MS = 2200;
const WORKING_CAP = 70;

/**
 * OTRIX Play — overlay de "generando tu rutina" del wizard: porcentaje simulado (0→70
 * mientras corren las mutations, 70→100 al resolver), frases rotativas que indican dónde
 * ajustar la rutina después, y arte CSS sobrio (anillos + barra dorada fina).
 *
 * phase: 'working' | 'done' | 'error'. En 'done' anima hasta 100 y dispara onComplete UNA vez.
 */
function GeneratingRoutineOverlay({ open, phase, onComplete, onRetry, onCancel }) {
  const { t } = useTranslation(['play']);
  const [progress, setProgress] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const completedRef = useRef(false);

  const phrases = t('onboarding.generating.phrases', {
    ns: 'play',
    returnObjects: true,
    defaultValue: [
      'Generando una buena rutina...',
      'Cruzando tu objetivo con la evidencia...',
      'Calculando series y descansos...',
      'Podrás ajustarla cuando quieras en Mi rutina.'
    ]
  });
  const phraseList = Array.isArray(phrases) ? phrases : [String(phrases)];

  // Reset al abrir.
  useEffect(() => {
    if (open) {
      setProgress(0);
      setPhraseIndex(0);
      completedRef.current = false;
    }
  }, [open]);

  // Progreso simulado: se acerca asintóticamente a 70 mientras trabaja; al terminar corre a 100.
  useEffect(() => {
    if (!open || phase === 'error') return undefined;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (phase === 'working') {
          const next = prev + Math.max(0.4, (WORKING_CAP - prev) * 0.055);
          return Math.min(WORKING_CAP, next);
        }
        return Math.min(100, prev + 4);
      });
    }, 90);

    return () => clearInterval(timer);
  }, [open, phase]);

  // Al llegar a 100 en 'done', avisar UNA vez (el padre cierra y navega).
  useEffect(() => {
    if (open && phase === 'done' && progress >= 100 && !completedRef.current) {
      completedRef.current = true;
      const t1 = setTimeout(() => onComplete(), 350);
      return () => clearTimeout(t1);
    }
    return undefined;
  }, [open, phase, progress, onComplete]);

  // Frases rotativas.
  useEffect(() => {
    if (!open || phase !== 'working') return undefined;
    const timer = setInterval(() => setPhraseIndex((i) => (i + 1) % phraseList.length), PHRASE_MS);
    return () => clearInterval(timer);
  }, [open, phase, phraseList.length]);

  const pct = Math.round(progress);

  return (
    <Fade in={open} timeout={{ enter: 200, exit: 300 }} unmountOnExit>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'surface.app',
          p: 3
        }}>
        <Stack spacing={4} alignItems="center" sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          {/* Anillos concéntricos con el % en el centro. */}
          <Box sx={{ position: 'relative', width: 180, height: 180, flex: 'none' }}>
            {[0, 1, 2].map((ring) => (
              <Box
                key={ring}
                sx={{
                  position: 'absolute',
                  inset: ring * 18,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: ring === 2 ? 'gold.main' : 'hairline.strong',
                  animation: `${ringPulse} ${2.4 + ring * 0.5}s cubic-bezier(0.32, 0.72, 0, 1) infinite`,
                  animationDelay: `${ring * 220}ms`
                }}
              />
            ))}
            <Stack
              spacing={0}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '2.6rem',
                  letterSpacing: '-0.03em',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1
                }}>
                {pct}%
              </Typography>
            </Stack>
          </Box>

          {phase === 'error' ? (
            <Stack spacing={2} alignItems="center">
              <Typography variant="h6" fontWeight={800}>
                {t('onboarding.generating.errorTitle', { ns: 'play', defaultValue: 'No pudimos armar tu rutina' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('onboarding.generating.errorBody', { ns: 'play', defaultValue: 'Revisa tu conexión e inténtalo de nuevo.' })}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={onCancel} color="inherit" sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
                  {t('onboarding.generating.back', { ns: 'play', defaultValue: 'Volver' })}
                </Button>
                <Button onClick={onRetry} sx={premiumButtonSx}>
                  {t('onboarding.generating.retry', { ns: 'play', defaultValue: 'Reintentar' })}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em">
                {t('onboarding.generating.title', { ns: 'play', defaultValue: 'Armando tu rutina' })}
              </Typography>
              {/* Frase rotativa (key fuerza el re-mount → animación de entrada). */}
              <Typography
                key={phase === 'done' ? 'done' : phraseIndex}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  minHeight: 40,
                  animation: `${phraseIn} 300ms cubic-bezier(0.32, 0.72, 0, 1)`
                }}>
                {phase === 'done'
                  ? t('onboarding.generating.done', { ns: 'play', defaultValue: 'Listo. Llevándote a tu rutina...' })
                  : phraseList[phraseIndex]}
              </Typography>
              {/* Barra dorada fina (solo transform). */}
              <Box sx={{ width: '100%', height: 3, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    borderRadius: 999,
                    backgroundColor: 'gold.main',
                    transform: `scaleX(${progress / 100})`,
                    transformOrigin: 'left',
                    transition: 'transform 120ms linear'
                  }}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    </Fade>
  );
}

GeneratingRoutineOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  phase: PropTypes.oneOf(['working', 'done', 'error']).isRequired,
  onComplete: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default GeneratingRoutineOverlay;
